import * as React from 'react';
import { COLORS } from '../styles';
import { SystemIcons } from '../icons';
import { LoadingIndicator } from '../LoadingIndicator';
import { DropdownInputFieldStyles, DropdownContainer, DropdownInputField } from './CommonStyling';
import DropdownContent, { DropdownItem } from './DropdownContent';
import { Size, Testable } from '../types';
import { AutofilledMessage, ErrorMessage } from '../InputFields';
import TooltipOverflow from '../Tooltips/TooltipOverflow';
import { defaultOnMouseDownHandler, useActionWithin, useClickOutsideRef, useFocusOutsideRef, useFocusVisibleRef } from '../common';
import { IconButton } from '../Button';

export interface DropdownFilterProps extends Testable, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onSelect' | 'size' | 'list' | 'autoComplete' | 'type' | 'placeholder' | 'onKeyDown' | 'value' | 'onChange' | 'required' | 'tabIndex' | 'disabled' | 'readOnly'> {
  list: DropdownItem[];
  placeholder?: string;
  
  value?: string;
  onSelect?: (value: string) => void;
  onInputChange?: (input: string) => void;

  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;

  disableFiltering?: boolean;

  messageOnNoResults?: string;
  activeValidationMessage?: string;
  autofilledMessage?: string;
  size?: Size.Small | Size.Medium;

  overflowTooltipPosition?: 'top' | 'bottom';
  buttonFontSize?: string;
  margin?: string;
  scrollable?: boolean;
}

const DropdownFilter = React.forwardRef( ({
                                            id,
                                            list,
                                            placeholder,
                                            onSelect,
                                            onInputChange,
                                            value,
                                            required,
                                            messageOnNoResults,
                                            disabled,
                                            readOnly,
                                            scrollable,
                                            activeValidationMessage,
                                            autofilledMessage,
                                            size,
                                            disableFiltering,
                                            buttonFontSize,
                                            margin,
                                            loading,
                                            onFocus,
                                            overflowTooltipPosition,
                                            onBlur,
                                            dataTestId,
                                            ...rest
                                          }: DropdownFilterProps, ref) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [input, setInput] = React.useState<string>();
  const [activeDescendant, setActiveDescendant] = React.useState<string>();
  const [restartFilter, setRestartFilter] = React.useState<boolean>(false);
  const [selectedItem, setSelectedItem] = React.useState<DropdownItem>();
  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [focused, setFocused] = React.useState<number | null>(null);
  const [inputFocused, setInputFocused] = React.useState<boolean>(false);
  const [init, setInit] = React.useState<boolean>(true);

  const styledFieldRef = useFocusVisibleRef();
  const inputRef = useFocusVisibleRef([styledFieldRef]);
  const clearButtonRef = React.useRef<HTMLButtonElement>(null);

  useActionWithin(styledFieldRef, input && !readOnly && !disabled ? [clearButtonRef] : []);

  React.useImperativeHandle(ref, () => inputRef.current, [inputRef]);

  const containerRef = useClickOutsideRef(
    () => setIsOpen(false),
    [],
    useFocusOutsideRef(() => setIsOpen(false)),
  );

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Esc' || e.key === 'Escape') {
      e.preventDefault();
    }
    if (e.key === 'ArrowDown' || e.key === 'Down' || e.key === 'ArrowUp' || e.key === 'Up') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  React.useEffect(() => {
    setInit(true);
    setSelectedValues(!!value ? [value] : []);
  }, [value]);

  React.useEffect(() => {
    if (selectedValues?.length) {
      const val = selectedValues[0];
      const item = list.find((a) => a.value === val);
      setSelectedItem(item);
      setInput(item?.displayLabel ?? item?.value ?? '');
      onSelect && !init && onSelect(val);
    } else {
      setSelectedItem(undefined);
      onSelect && !init && onSelect('');
      setInput('');
    }
  }, [selectedValues]);

  React.useEffect(() => {
    if (!isOpen) {
      if (!!selectedItem) {
        setInput(selectedItem?.displayLabel ?? selectedItem?.value ?? '');
      } else {
        setInput('');
      }
    }

    setRestartFilter(true);
  }, [isOpen, selectedItem]);

  const handleInputClear = (e: any) => {
    setInput('');
    setSelectedValues([]);
    onSelect && onSelect('');
    inputRef.current?.focus({ focusVisible: e?.detail !== 1 } as any);
  };

  const renderInput = () => {
    return (
      <DropdownInputFieldStyles
        ref={styledFieldRef}
        className={size ? size : ''}
        onClick={(e: any) => {
          if (!readOnly && !disabled) {
            e.stopPropagation();
            setIsOpen(!isOpen);
            if (isOpen) {
              inputRef.current?.blur();
            } else {
              inputRef.current?.focus();
            }
          }
        }}
        disabled={disabled || false}
        readOnly={readOnly || false}
        isPlaceholder={!input}
        fontSize={buttonFontSize}
        showValidationMessage={!!activeValidationMessage}
        onMouseDown={defaultOnMouseDownHandler}
        data-testid={`otherdiv_${id}`}>
        <DropdownInputField
          id={id}
          ref={inputRef}
          autoComplete={'off-' + Math.floor(Math.random() * 999999999999)}
          type="search"
          placeholder={inputFocused ? '' : placeholder}
          onKeyDown={handleInputKeyDown}
          ellipsis
          value={input || ''}
          onChange={(e: any) => {
            if (!e.target.value) {
              setSelectedValues([]);
            }
            if (!readOnly && !disabled && e.target.value !== input) {
              setRestartFilter(false);
              setIsOpen(true);
              setInput(e.target.value);
              if (onInputChange) {
                onInputChange(e.target.value || '');
              }
            }
          }}
          onFocus={(e) => {
            setInputFocused(true);
            onFocus && onFocus(e);
          }}
          onBlur={(e) => {
            setInputFocused(false);
            onBlur && onBlur(e);
          }}
          required={required}
          tabIndex={disabled || readOnly ? -1 : 0}
          disabled={disabled || false}
          data-testid={dataTestId}
          {...rest}
        />
        {!isOpen ? <TooltipOverflow position={overflowTooltipPosition} input={inputRef} withArrow={true} maxWidth="100%" size={size} align='end'>
            {input}
          </TooltipOverflow> : null}
        {input && !readOnly && !disabled && !loading && (
          <IconButton action={handleInputClear} ref={clearButtonRef} useTransparentBackground={true} shape={'circular'} style={{ marginLeft: '-4px' }} variant={'secondary'}>
            <SystemIcons.Clear />
          </IconButton>
        )}

        {!disabled && !readOnly && loading && (
          <div style={{ marginLeft: '-4xp' }}>
            <LoadingIndicator size={Size.Small} color={COLORS.neutral_600} />
          </div>
        )}

        <div className={'dropdown-arrow'}>
          {isOpen && !readOnly && !disabled ? <SystemIcons.ArrowDropUp size="24px" className={size ? size : ''} /> : <SystemIcons.ArrowDropDown size="24px" className={size ? size : ''} />}
        </div>
      </DropdownInputFieldStyles>
    );
  };

  return (
    <>
      <DropdownContainer ref={containerRef}
      role="combobox"
      aria-multiselectable={true} 
      aria-controls={`${id}_dropdowncontent`}
      aria-activedescendant={activeDescendant}
      aria-expanded={isOpen}
      disabled={disabled} isButton={false} readOnly={readOnly} className={size ? size : ''} margin={margin}>
        {renderInput()}
        {!readOnly && !disabled && (
          <DropdownContent
            containerRef={containerRef}
            isOpen={isOpen && !loading}
            messageOnNoResults={messageOnNoResults ?? ''}
            selectedValues={selectedValues}
            setSelectedValues={setSelectedValues}
            size={size ?? Size.Small}
            onActiveDescendantChanged={(e) => setActiveDescendant(e)}
            focused={focused}
            ariaRolesType="input"
            setFocused={setFocused}
            outline={false}
            customizationProps={{
              itemsType: 'normal',
              scrollable: scrollable,
              multiSelect: false,
              action: () => {},
              onValueUpdate: (values: string[]) => {
                setRestartFilter(true);
                setInit(false);
              },
              items: list,
            }}
            filter={!disableFiltering && !restartFilter ? input || '' : ''}
            setIsOpen={setIsOpen}
            isButton={false}
            id={`${id}_dropdowncontent`}
          />
        )}
      </DropdownContainer>
      {activeValidationMessage && (
        <ErrorMessage className={size || ''}>
          <SystemIcons.TechnicalWarning color={COLORS.critical_400} />
          <span>{activeValidationMessage}</span>
        </ErrorMessage>
      )}
      {autofilledMessage && (
        <AutofilledMessage className={size || ''}>
          <SystemIcons.Information color={COLORS.neutral_600} />
          <span>{autofilledMessage}</span>
        </AutofilledMessage>
      )}
    </>
  );
});

export default DropdownFilter;
